/**
 * The timezone list, taken from the platform rather than written down — and made readable.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THIS IS NOT A HARDCODED LIST OF NORWEGIAN ZONES. Principle 9: nothing org-specific is
 * hardcoded. A curated list would be a guess about which customers Tvitvi will have, and the
 * first festival in Reykjavík or Berlin would meet a picker that cannot express where it is.
 * `Intl.supportedValuesOf('timeZone')` is the IANA database the browser already ships, so the
 * list is complete, localised by nobody, and costs no bytes.
 *
 * WHY THE FIELD STILL VALIDATES. `createFestivalRequestSchema.timezone` is
 * `z.string().min(1).max(64)` — a length check, not an identifier check — so "Bananas" is a
 * valid request as far as the contract is concerned, and every operational time on the festival
 * would then be read in a zone that does not exist. The suggestion list narrows what is easy to
 * type; this predicate is what refuses the rest, and it is checked on the way OUT of the form
 * rather than inside the resolver so that the shared schema stays the single description of what
 * the API accepts.
 *
 * IF THE RUNTIME HAS NO LIST, NOTHING IS REFUSED. `supportedValuesOf` landed in every current
 * browser and in Node 18, but a validator that rejects every value because it could not build
 * its own vocabulary is worse than no validator: it would make the field unfillable. So an empty
 * catalogue means "cannot check", not "nothing is valid".
 *
 * ---------------------------------------------------------------------------------------------
 * «Europe/Oslo» ER EN IDENT, IKKE ET NAVN (QA-runde 12, #16a og 17f).
 *
 * Atle så et felt som sto låst på `Europe/Oslo` og et faktum på festivalsiden som sto i monospace.
 * Begge er den samme feilen: identen er databasens nøkkel, og den er det eneste som ble vist. En
 * leser skal kjenne igjen STEDET og se hvilken klokke det er — «Oslo (UTC+1/+2)» — og
 * `timezoneLabel` er den ene setningen som lager den, brukt av både velgeren og festivalsiden slik
 * at de to aldri kan si det ulikt.
 *
 * FORSKYVNINGENE REGNES, DE STÅR IKKE I EN TABELL. To målinger per sone — 15. januar og 15. juli —
 * er nok til å skille en sone med sommertid fra en uten, og de leses av den samme IANA-basen som
 * ga lista. En håndskrevet tabell over forskyvninger er en tabell som blir feil den dagen et land
 * flytter seg, og det skjer flere ganger i året et sted i verden.
 *
 * DE REGNES BARE FOR DET SOM VISES. `Intl.DateTimeFormat` er dyr å lage, og katalogen har over
 * fire hundre soner: å merke alle ved lasting ville vært åtte hundre formattere for en liste ingen
 * har åpnet ennå. `timezoneOptions` merker de åtte som vises, `timezoneLabel` den ene som står i
 * feltet, og svarene huskes i en `Map` slik at den samme sonen aldri måles to ganger.
 * ---------------------------------------------------------------------------------------------
 */

/** How many suggestions the panel offers. More than a screenful is a list nobody reads. */
export const MAX_TIMEZONE_SUGGESTIONS = 8;

const CATALOGUE: readonly string[] =
  typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];

export function isKnownTimezone(value: string): boolean {
  if (CATALOGUE.length === 0) return true;
  return CATALOGUE.includes(value);
}

/**
 * Zones matching what has been typed so far, best-first.
 *
 * A prefix match ranks above an anywhere-match: somebody typing "Osl" means Oslo, and
 * "America/Los_Angeles" containing "os" is not a better answer than "Europe/Oslo" starting with
 * it. Case-insensitive because nobody types the underscore-and-capital form from memory.
 */
export function timezoneSuggestions(
  query: string,
  limit: number = MAX_TIMEZONE_SUGGESTIONS,
): readonly string[] {
  const needle = query.trim().toLowerCase().replace(/\s+/g, '_');
  if (needle.length === 0) return CATALOGUE.slice(0, limit);

  const starts: string[] = [];
  const contains: string[] = [];
  for (const zone of CATALOGUE) {
    const haystack = zone.toLowerCase();
    // The segment after the region matters most: "oslo" should find "Europe/Oslo".
    const city = haystack.slice(haystack.lastIndexOf('/') + 1);
    if (haystack.startsWith(needle) || city.startsWith(needle)) starts.push(zone);
    else if (haystack.includes(needle)) contains.push(zone);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

/** The city an IANA id ends in, with the underscores that are an id's and not a reader's. */
export function timezoneCity(zone: string): string {
  const last = zone.slice(zone.lastIndexOf('/') + 1);
  return last.replace(/_/g, ' ');
}

/** The region an id starts with — only shown when two cities in the list share a name. */
function timezoneRegion(zone: string): string {
  const cut = zone.indexOf('/');
  return cut === -1 ? '' : zone.slice(0, cut).replace(/_/g, ' ');
}

/** Measured once per zone and remembered — see the header on why they are not measured at all. */
const OFFSETS = new Map<string, string>();

/**
 * `GMT+1` and `GMT+05:30` as the platform writes them, rendered the way a reader says them.
 *
 * `GMT` alone means zero, and "UTC" rather than "GMT" because that is the abbreviation the
 * industry writes on a day sheet.
 */
function offsetAt(zone: string, monthIndex: number): string | null {
  try {
    const at = new Date(Date.UTC(2026, monthIndex, 15, 12));
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    const raw = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (raw === undefined) return null;
    const stripped = raw.replace(/^(GMT|UTC)/, '');
    return stripped === '' ? '+0' : stripped;
  } catch {
    // An id the runtime cannot format has no clock to state. The city is still the answer.
    return null;
  }
}

/**
 * The two offsets a zone runs on, as «+1/+2» — or as «+1» when it never moves.
 *
 * Returns `null` when the runtime could not answer, which is the same case as a zone id nobody
 * recognises: the label is then the city alone rather than a parenthesis with nothing in it.
 */
function offsetsOf(zone: string): string | null {
  const remembered = OFFSETS.get(zone);
  if (remembered !== undefined) return remembered === '' ? null : remembered;

  const winter = offsetAt(zone, 0);
  const summer = offsetAt(zone, 6);
  const text =
    winter === null ? '' : summer === null || summer === winter ? winter : `${winter}/${summer}`;
  OFFSETS.set(zone, text);
  return text === '' ? null : text;
}

/**
 * «Oslo (UTC+1/+2)» — the whole of what a reader needs from a zone.
 *
 * Used by the picker AND by the festival page's own facts line, so the two cannot describe the
 * same zone differently (17f). A zone id that is not in the catalogue is still rendered rather
 * than refused: a festival created before this field existed must read as something.
 */
export function timezoneLabel(zone: string): string {
  if (zone === '') return '';
  const city = timezoneCity(zone);
  const offsets = offsetsOf(zone);
  return offsets === null ? city : `${city} (UTC${offsets})`;
}

/** One row of the picker: what is written to the festival, and what the reader reads. */
export interface TimezoneOption {
  /** The IANA id — the value that is stored. */
  zone: string;
  /** «Oslo (UTC+1/+2)» — the value that is shown and typed. */
  label: string;
}

/**
 * The suggestions for what has been typed, as readable rows.
 *
 * TWO CITIES WITH THE SAME NAME GET THEIR REGION BACK. `Asia/Nicosia` and `Europe/Nicosia` are
 * both real, share a clock, and would otherwise be two identical rows — a list where picking
 * either one is a coin toss. The region is added only to the rows that collide, so the ordinary
 * case stays «Oslo (UTC+1/+2)» and the ambiguous one says which Nicosia it is.
 */
export function timezoneOptions(
  query: string,
  limit: number = MAX_TIMEZONE_SUGGESTIONS,
): readonly TimezoneOption[] {
  const zones = timezoneSuggestions(query, limit);
  const rows = zones.map((zone) => ({ zone, label: timezoneLabel(zone) }));

  const seen = new Map<string, number>();
  for (const row of rows) seen.set(row.label, (seen.get(row.label) ?? 0) + 1);

  return rows.map((row) => {
    if ((seen.get(row.label) ?? 0) < 2) return row;
    const region = timezoneRegion(row.zone);
    return region === '' ? row : { zone: row.zone, label: `${region} · ${row.label}` };
  });
}

/**
 * What the reader typed, turned back into an IANA id — or `null` when it is not one.
 *
 * Three things are accepted, in this order: the id itself (somebody pasting `Europe/Oslo` from a
 * config file), a label this module produced, and a bare city name. The last is what makes the
 * field forgiving in the case that matters — typing "Oslo" and pressing Lagre without opening the
 * panel — and it is deliberately an EXACT city match rather than a prefix: "Sao" resolving to São
 * Paulo would be the field guessing on somebody's behalf about which clock a festival runs on.
 */
export function resolveTimezone(text: string): string | null {
  const needle = text.trim();
  if (needle === '') return null;
  if (isKnownTimezone(needle) && CATALOGUE.includes(needle)) return needle;

  const lower = needle.toLowerCase();
  for (const zone of CATALOGUE) {
    if (zone.toLowerCase() === lower) return zone;
  }
  for (const zone of CATALOGUE) {
    if (timezoneLabel(zone).toLowerCase() === lower) return zone;
    if (timezoneCity(zone).toLowerCase() === lower) return zone;
  }
  return null;
}

/**
 * The zone a brand-new festival opens on: the reader's own, when the runtime knows it.
 *
 * #16a: «Forhåndsvelg fra org eller nettleser.» The organisation's zone is not on `/v1/me` — it
 * is a column on `organizations` with no endpoint the shell reads — so the caller passes what it
 * has (the user's own setting, which is seeded from the organisation's when the account is made)
 * and this prefers the BROWSER over it, because a browser that says Europe/Berlin is a person who
 * is in Berlin. Both are proposals: the field is a combobox and the first thing a reader does
 * with a wrong guess is change it.
 */
export function preferredTimezone(fallback: string): string {
  try {
    const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof guess === 'string' && guess !== '' && isKnownTimezone(guess)) return guess;
  } catch {
    // A runtime with no resolved zone is a runtime that cannot guess. The fallback is the answer.
  }
  return fallback;
}
